<template>
  <div>
    <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
      <div class="row mb-2">
        <div class="col">
          <h1 class="text-white pt-3 px-3">ประกาศข่าวสาร</h1>
          <p class="text-white pb-3 px-3 mb-0">ประกาศและโปรโมชันที่เคยแสดงในหน้า POS ย้อนหลัง 12 เดือน</p>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--8 mb-5">
      <div class="card shadow">
        <div class="card-body">
          <div class="ann-tools">
            <div class="ann-seg" role="group" aria-label="สถานะ">
              <button
                v-for="s in statuses"
                :key="s.value"
                type="button"
                :aria-pressed="String(status === s.value)"
                @click="setStatus(s.value)"
              >{{ s.label }}</button>
            </div>
            <label class="ann-search" for="annSearch">
              <i class="fas fa-search" aria-hidden="true"></i>
              <input
                id="annSearch"
                v-model="searchInput"
                type="search"
                maxlength="100"
                placeholder="ค้นหาชื่อประกาศ"
                aria-label="ค้นหาชื่อประกาศ"
              />
            </label>
          </div>

          <div aria-live="polite">
            <template v-if="loading && !items.length">
              <div v-for="n in 3" :key="'s' + n" class="ann-skel"></div>
            </template>

            <div v-else-if="error && !items.length" class="ann-state">
              <b>โหลดประกาศไม่สำเร็จ</b>
              ตรวจสอบการเชื่อมต่ออินเทอร์เน็ต แล้วลองอีกครั้ง
              <div class="mt-3"><button type="button" class="btn btn-sm btn-outline-primary" @click="load(false)">ลองใหม่</button></div>
            </div>

            <div v-else-if="!items.length" class="ann-state">
              <template v-if="status !== 'all' || search">
                <b>ไม่พบประกาศที่ตรงกับตัวกรอง</b>
                ลองเปลี่ยนสถานะหรือคำค้นหา
                <div class="mt-3"><button type="button" class="btn btn-sm btn-outline-primary" @click="clearFilters">ล้างตัวกรอง</button></div>
              </template>
              <template v-else>
                <b>ยังไม่มีประกาศย้อนหลัง</b>
                ประกาศและโปรโมชันที่แสดงในหน้า POS จะมารวมไว้ที่นี่
              </template>
            </div>

            <template v-else>
              <section v-for="group in groups" :key="group.key">
                <div class="ann-month">{{ group.key }}</div>
                <div class="ann-feed">
                  <button
                    v-for="c in group.items"
                    :key="c.id"
                    type="button"
                    class="ann-card"
                    :class="{ 'is-ended': !isLive(c), 'is-text': !imageOf(c) }"
                    aria-haspopup="dialog"
                    @click="openDetail(c)"
                  >
                    <img v-if="imageOf(c)" class="ann-img" :src="imageOf(c)" :alt="c.name || ''" loading="lazy" />
                    <div class="ann-body">
                      <div class="ann-meta">
                        <span v-if="typeName(c)" class="ann-badge type" :class="'type-' + typeCode(c)">{{ typeName(c) }}</span>
                        <span v-if="isLive(c)" class="ann-badge live">ใช้งานอยู่</span>
                        <span v-else class="ann-badge ended">สิ้นสุดแล้ว</span>
                        <span v-if="period(c)">{{ period(c) }}</span>
                      </div>
                      <div class="ann-title">{{ c.name }}</div>
                      <div v-if="c.note" class="ann-desc">{{ c.note }}</div>
                      <div v-if="safeUrl(c.url)" class="ann-hint">ดูรายละเอียด</div>
                    </div>
                  </button>
                </div>
              </section>

              <div v-if="error" class="ann-state">
                <b>โหลดประกาศไม่สำเร็จ</b>
                <div class="mt-2"><button type="button" class="btn btn-sm btn-outline-primary" @click="load(true)">ลองใหม่</button></div>
              </div>
              <div v-else-if="page < totalPage" class="text-center mt-4">
                <button type="button" class="btn btn-outline-primary" :disabled="loading" @click="loadMore">
                  {{ loading ? "กำลังโหลด..." : "โหลดเพิ่ม" }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <b-modal v-model="detailOpen" hide-header centered body-class="p-0" :title="detail ? detail.name : ''">
      <template v-if="detail">
        <img v-if="imageOf(detail)" class="ann-m-img" :src="imageOf(detail)" :alt="detail.name || ''" />
        <div class="ann-m-in">
          <div class="ann-meta">
            <span v-if="typeName(detail)" class="ann-badge type" :class="'type-' + typeCode(detail)">{{ typeName(detail) }}</span>
            <span v-if="isLive(detail)" class="ann-badge live">ใช้งานอยู่</span>
            <span v-else class="ann-badge ended">สิ้นสุดแล้ว</span>
            <span v-if="period(detail)">{{ period(detail) }}</span>
          </div>
          <h4 class="ann-m-title">{{ detail.name }}</h4>
          <div class="ann-m-note">{{ detail.note }}</div>
          <div v-if="!isLive(detail) && safeUrl(detail.url)" class="ann-m-expired">
            แคมเปญนี้หมดอายุแล้ว<template v-if="detail.end"> (สิ้นสุด {{ dShort(toDate(detail.end)) }})</template>
            เงื่อนไขในหน้าปลายทางอาจใช้ไม่ได้แล้ว
          </div>
        </div>
      </template>
      <template slot="modal-footer">
        <button type="button" class="btn btn-secondary" @click="detailOpen = false">ปิด</button>
        <a
          v-if="detail && safeUrl(detail.url)"
          class="btn btn-primary"
          :href="safeUrl(detail.url)"
          target="_blank"
          rel="noopener noreferrer"
          @click="trackClick(detail)"
        >ไปที่หน้าโปรโมชัน <i class="fas fa-external-link-alt" aria-hidden="true"></i></a>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { getAnnouncements, markAllActiveRead, trackAnnouncementClick } from "@/axios/announcement.js";

const MTH = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const MTHF = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const TYPE_NAME = { PR: "โปรโมชัน", AN: "ประกาศ", NE: "ข่าวสาร", CO: "โปรร่วมขนส่ง", AD: "โฆษณา", BL: "บทความ" };
const LIMIT = 12;

export default {
  name: "Announcement",
  data() {
    return {
      statuses: [
        { value: "all", label: "ทั้งหมด" },
        { value: "active", label: "ใช้งานอยู่" },
        { value: "expired", label: "สิ้นสุดแล้ว" },
      ],
      status: "all",
      searchInput: "",
      search: "",
      searchTimer: null,
      items: [],
      page: 1,
      totalPage: 0,
      clickPositionId: 0,
      loading: false,
      error: false,
      requestSeq: 0,
      detail: null,
      detailOpen: false,
    };
  },
  computed: {
    groups() {
      const out = [];
      this.items.forEach((c) => {
        const d = this.toDate(c.start) || this.toDate(c.end);
        const key = d ? MTHF[d.getMonth()] + " " + (d.getFullYear() + 543) : "ไม่ระบุวันที่";
        const last = out[out.length - 1];
        if (last && last.key === key) last.items.push(c);
        else out.push({ key, items: [c] });
      });
      return out;
    },
  },
  watch: {
    searchInput(v) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        const s = v.trim();
        if (s === this.search) return;
        this.search = s;
        this.load(false);
      }, 350);
    },
  },
  mounted() {
    this.load(false);
    // opening this page reads every running announcement -> clears the navbar unread count
    markAllActiveRead();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    load(append) {
      const seq = ++this.requestSeq; // ignore responses from superseded filters
      if (!append) {
        this.page = 1;
        this.items = [];
      }
      this.loading = true;
      this.error = false;
      getAnnouncements({ status: this.status, search: this.search, page: this.page, limit: LIMIT, nocache: 1 })
        .then((res) => {
          if (seq !== this.requestSeq) return;
          const meta = res.metadata || {};
          const rows = Array.isArray(res.result) ? res.result : [];
          this.totalPage = meta.total_page || 0;
          this.clickPositionId = meta.click_position_id || 0;
          this.items = append ? this.items.concat(rows) : rows;
        })
        .catch(() => {
          if (seq !== this.requestSeq) return;
          if (append) this.page--;
          this.error = true;
        })
        .finally(() => {
          if (seq === this.requestSeq) this.loading = false;
        });
    },
    loadMore() {
      this.page++;
      this.load(true);
    },
    setStatus(s) {
      if (this.status === s) return;
      this.status = s;
      this.load(false);
    },
    clearFilters() {
      this.status = "all";
      this.searchInput = "";
      this.search = "";
      this.load(false);
    },
    openDetail(c) {
      this.detail = c;
      this.detailOpen = true;
    },
    trackClick(c) {
      trackAnnouncementClick(c.id, this.clickPositionId);
    },
    isLive(c) {
      return c.status_result === "Active";
    },
    typeCode(c) {
      return c.type && c.type.type ? c.type.type : "";
    },
    typeName(c) {
      return TYPE_NAME[this.typeCode(c)] || "";
    },
    imageOf(c) {
      const imgs = c.images || {};
      return imgs.DF || imgs.M || imgs.BM || Object.values(imgs)[0] || "";
    },
    safeUrl(u) {
      return typeof u === "string" && /^https?:\/\//i.test(u) ? u : "";
    },
    toDate(s) {
      return s ? new Date(s) : null;
    },
    dShort(d) {
      return d.getDate() + " " + MTH[d.getMonth()] + " " + (d.getFullYear() + 543);
    },
    period(c) {
      const a = this.toDate(c.start);
      const b = this.toDate(c.end);
      if (a && b) {
        if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
          return (a.getDate() === b.getDate() ? "" : a.getDate() + "–") + this.dShort(b);
        }
        return a.getDate() + " " + MTH[a.getMonth()] + " – " + this.dShort(b);
      }
      if (a) return "ตั้งแต่ " + this.dShort(a);
      if (b) return "ถึง " + this.dShort(b);
      return "";
    },
  },
};
</script>

<style scoped>
.ann-tools { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.ann-seg { display: inline-flex; border: 1px solid #e0e0e0; border-radius: 6px; overflow: hidden; background: #fff; }
.ann-seg button { border: 0; background: transparent; color: #525f7f; font-size: 14px; padding: 6px 14px; min-height: 36px; cursor: pointer; }
.ann-seg button[aria-pressed="true"] { background: #e3f4fb; color: #0773a0; font-weight: 600; }
.ann-search { margin: 0 0 0 auto; position: relative; flex: 1 1 220px; max-width: 280px; }
.ann-search input { width: 100%; height: 38px; border: 1px solid #e0e0e0; border-radius: 6px; padding: 6px 10px 6px 34px; font-size: 14px; }
.ann-search .fas { position: absolute; left: 12px; top: 12px; color: #999; }
.ann-seg button:focus-visible, .ann-search input:focus, .ann-card:focus-visible { outline: 2px solid #0773a0; outline-offset: 2px; }
.ann-month { font-size: 13px; font-weight: 600; color: #6b6b6b; margin: 24px 0 10px; display: flex; align-items: center; gap: 10px; }
.ann-month:after { content: ""; flex: 1; height: 1px; background: #e9ecef; }
.ann-feed { display: grid; gap: 12px; }
.ann-card { display: grid; grid-template-columns: 200px 1fr; width: 100%; padding: 0; text-align: left; background: #fff; border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; cursor: pointer; color: inherit; transition: border-color 0.18s ease, box-shadow 0.18s ease; }
.ann-card:hover { border-color: #11cdef; box-shadow: 0 4px 14px rgba(17, 205, 239, 0.15); }
.ann-card.is-text { grid-template-columns: 1fr; }
.ann-img { display: block; width: 100%; aspect-ratio: 16 / 10; object-fit: cover; background: #f1f1f1; } /* DF banners are 800x500 */
.ann-card.is-ended .ann-img { filter: saturate(0.55); }
.ann-body { padding: 12px 16px; min-width: 0; }
.ann-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 12.5px; color: #6b6b6b; }
.ann-title { font-weight: 600; font-size: 15.5px; color: #32325d; margin-top: 4px; }
.ann-desc { font-size: 13.5px; color: #6b6b6b; margin-top: 2px; white-space: pre-line; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.ann-hint { font-size: 12.5px; color: #0773a0; font-weight: 600; margin-top: 4px; }
.ann-badge { font-size: 11.5px; font-weight: 600; padding: 1px 8px; border-radius: 4px; white-space: nowrap; }
.ann-badge.live { background: #fef3c7; color: #8a4b08; } /* ใช้งานอยู่: yellow */
.ann-badge.ended { background: #ececec; color: #5e5e5e; } /* สิ้นสุดแล้ว: grey */
.ann-badge.type { border: 1px solid currentColor; color: #0773a0; }
.ann-badge.type-PR { color: #c2410c; }
.ann-badge.type-NE { color: #4f5bb5; }
.ann-badge.type-CO { color: #9a3e8f; }
.ann-badge.type-AD, .ann-badge.type-BL { color: #5e5e5e; }
.ann-state { text-align: center; padding: 36px 12px; color: #6b6b6b; border: 1px dashed #e0e0e0; border-radius: 8px; margin-top: 20px; }
.ann-state b { color: #32325d; display: block; margin-bottom: 4px; }
.ann-skel { height: 125px; margin-top: 12px; border-radius: 8px; background: linear-gradient(90deg, #eee 25%, #f6f6f6 37%, #eee 63%); background-size: 400% 100%; animation: ann-sh 1.4s ease infinite; }
@keyframes ann-sh { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
.ann-m-img { display: block; width: 100%; height: auto; border-radius: 0.4375rem 0.4375rem 0 0; }
.ann-m-in { padding: 16px 20px 8px; }
.ann-m-title { font-size: 19px; margin: 8px 0; }
.ann-m-note { white-space: pre-line; color: #525f7f; }
.ann-m-expired { margin-top: 14px; padding: 8px 12px; border-radius: 6px; background: #f1f1f1; color: #5e5e5e; font-size: 13px; }
@media (max-width: 640px) {
  .ann-search { margin-left: 0; max-width: none; }
  .ann-card { grid-template-columns: 112px 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .ann-card { transition: none; }
  .ann-skel { animation: none; }
}
</style>
